// Australian Pharmacy Board Compliance Data
// Based on https://www.pharmacyboard.gov.au/Codes-Guidelines.aspx

export interface ComplianceRequirement {
  id: string;
  category: 'code_of_conduct' | 'dispensing' | 's8_management' | 'compounding' | 'advertising' | 'notifications' | 'cpd' | 'cosmetic_procedures';
  title: string;
  description: string;
  regulatorySource: string;
  effectiveDate: string;
  requirements: string[];
  penalties: string[];
  auditFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  lastAuditDate?: string;
  nextAuditDate: string;
  complianceStatus: 'compliant' | 'non_compliant' | 'under_review' | 'action_required';
  actionItems: ComplianceAction[];
}

export interface ComplianceAction {
  id: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  dueDate: string;
  assignedTo: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  evidence?: string[];
}

export interface MandatoryNotification {
  id: string;
  type: 'impairment' | 'professional_misconduct' | 'criminal_conviction' | 'significant_departure';
  reporterName: string;
  reporterRole: string;
  practitionerName: string;
  practitionerRegistration: string;
  incidentDate: string;
  reportDate: string;
  description: string;
  status: 'draft' | 'submitted' | 'under_investigation' | 'resolved';
  urgency: 'immediate' | 'within_24_hours' | 'within_30_days';
  evidence: string[];
  followUpRequired: boolean;
}

export interface S8ComplianceRecord {
  id: string;
  date: string;
  type: 'storage_audit' | 'register_reconciliation' | 'destruction_witness' | 'access_control' | 'discrepancy_investigation';
  findings: string[];
  complianceStatus: 'compliant' | 'minor_issues' | 'major_issues' | 'critical_breach';
  correctiveActions: string[];
  witnessName?: string;
  witnessSignature?: string;
  pharmacistName: string;
  pharmacistSignature: string;
  nextAuditDate: string;
}

export interface CPDRecord {
  id: string;
  pharmacistId: string;
  pharmacistName: string;
  activityType: 'formal_learning' | 'informal_learning' | 'practice_review' | 'professional_activity';
  activityTitle: string;
  provider: string;
  date: string;
  hours: number;
  learningOutcomes: string[];
  reflectionNotes: string;
  evidence: string[];
  approvedBy?: string;
  cpdYear: string;
}

// Australian Pharmacy Board Compliance Requirements
export const complianceRequirements: ComplianceRequirement[] = [
  {
    id: 'comp-001',
    category: 'code_of_conduct',
    title: 'Code of Conduct for Registered Health Practitioners',
    description: 'Adherence to professional standards and ethical conduct as outlined in the national code',
    regulatorySource: 'Pharmacy Board of Australia - Code of Conduct (29 June 2022)',
    effectiveDate: '2022-06-29',
    requirements: [
      'Provide safe and competent care',
      'Practice within scope of practice',
      'Maintain professional boundaries',
      'Obtain informed consent',
      'Maintain confidentiality and privacy',
      'Avoid conflicts of interest',
      'Report concerns about patient safety',
      'Maintain professional competence through CPD'
    ],
    penalties: [
      'Caution or reprimand',
      'Conditions on registration',
      'Suspension of registration',
      'Cancellation of registration'
    ],
    auditFrequency: 'annually',
    nextAuditDate: '2024-12-31',
    complianceStatus: 'compliant',
    actionItems: []
  },
  {
    id: 'comp-002',
    category: 'dispensing',
    title: 'Guidelines for Dispensing of Medicines',
    description: 'Standards for safe and accurate dispensing practices',
    regulatorySource: 'Pharmacy Board of Australia - Guidelines for Dispensing (15 March 2023)',
    effectiveDate: '2023-03-15',
    requirements: [
      'Verify prescription authenticity and legality',
      'Check patient identity and medication history',
      'Ensure appropriate storage conditions',
      'Provide clear labeling and patient information',
      'Maintain dispensing records for 3 years',
      'Ensure competent staff supervision',
      'Implement quality assurance procedures'
    ],
    penalties: [
      'Professional standards breach',
      'Registration conditions',
      'Mandatory training requirements'
    ],
    auditFrequency: 'monthly',
    nextAuditDate: '2024-10-15',
    complianceStatus: 'compliant',
    actionItems: [
      {
        id: 'action-001',
        description: 'Update dispensing SOP documentation',
        priority: 'medium',
        dueDate: '2024-10-01',
        assignedTo: 'Senior Pharmacist',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 'comp-003',
    category: 's8_management',
    title: 'Schedule 8 Poisons Management',
    description: 'Secure storage, accurate records, and proper handling of controlled substances',
    regulatorySource: 'Victorian Pharmacy Authority - Managing Schedule 8 Poisons (24 March 2021)',
    effectiveDate: '2021-03-24',
    requirements: [
      'Store S8 poisons in approved drug safe',
      'Maintain accurate drug register records',
      'Ensure safe keys are strictly controlled',
      'Conduct regular stock reconciliations',
      'Report discrepancies to MPR (DHHS)',
      'Witness destruction of expired S8 medicines',
      'Notify authorities of loss or theft immediately',
      'Retain records for 3 years in retrievable form'
    ],
    penalties: [
      'Immediate notification to Victoria Police',
      'MPR (DHHS) investigation',
      'Potential criminal charges',
      'Registration suspension or cancellation'
    ],
    auditFrequency: 'weekly',
    nextAuditDate: '2024-09-22',
    complianceStatus: 'compliant',
    actionItems: [
      {
        id: 'action-002',
        description: 'Weekly S8 register reconciliation',
        priority: 'high',
        dueDate: '2024-09-22',
        assignedTo: 'Pharmacist in Charge',
        status: 'pending'
      }
    ]
  },
  {
    id: 'comp-004',
    category: 'compounding',
    title: 'Guidelines on Compounding of Medicines',
    description: 'Standards for preparation of compounded medicines',
    regulatorySource: 'Pharmacy Board of Australia - Compounding Guidelines (1 October 2024)',
    effectiveDate: '2024-10-01',
    requirements: [
      'Ensure appropriate facilities and equipment',
      'Maintain sterile compounding capabilities',
      'Document all compounding procedures',
      'Implement quality control testing',
      'Maintain stability and expiry data',
      'Ensure staff competency and training',
      'Keep detailed batch records'
    ],
    penalties: [
      'Compounding license suspension',
      'Mandatory facility upgrades',
      'Additional training requirements'
    ],
    auditFrequency: 'quarterly',
    nextAuditDate: '2024-12-01',
    complianceStatus: 'action_required',
    actionItems: [
      {
        id: 'action-003',
        description: 'Update compounding SOPs to new guidelines',
        priority: 'critical',
        dueDate: '2024-10-31',
        assignedTo: 'Compounding Pharmacist',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 'comp-005',
    category: 'notifications',
    title: 'Mandatory Notifications',
    description: 'Requirements for reporting notifiable conduct',
    regulatorySource: 'Pharmacy Board of Australia - Mandatory Notifications (1 March 2020)',
    effectiveDate: '2020-03-01',
    requirements: [
      'Report impairment affecting practice',
      'Report professional misconduct',
      'Report criminal convictions',
      'Report significant departures from standards',
      'Submit notifications within required timeframes',
      'Provide accurate and complete information',
      'Cooperate with investigations'
    ],
    penalties: [
      'Failure to notify is professional misconduct',
      'Registration sanctions',
      'Mandatory training'
    ],
    auditFrequency: 'annually',
    nextAuditDate: '2025-03-01',
    complianceStatus: 'compliant',
    actionItems: []
  },
  {
    id: 'comp-006',
    category: 'cpd',
    title: 'Continuing Professional Development',
    description: 'Ongoing learning and professional development requirements',
    regulatorySource: 'Pharmacy Board of Australia - CPD Registration Standard',
    effectiveDate: '2024-01-01',
    requirements: [
      'Complete minimum 40 hours CPD annually',
      'Include formal and informal learning',
      'Maintain CPD portfolio',
      'Reflect on learning outcomes',
      'Ensure relevance to practice',
      'Submit annual CPD summary'
    ],
    penalties: [
      'Registration renewal conditions',
      'Mandatory additional CPD',
      'Practice restrictions'
    ],
    auditFrequency: 'annually',
    nextAuditDate: '2024-12-31',
    complianceStatus: 'compliant',
    actionItems: [
      {
        id: 'action-004',
        description: 'Complete remaining 15 hours CPD for 2024',
        priority: 'medium',
        dueDate: '2024-12-31',
        assignedTo: 'All Pharmacists',
        status: 'in_progress'
      }
    ]
  }
];

// Sample S8 Compliance Records
export const s8ComplianceRecords: S8ComplianceRecord[] = [
  {
    id: 's8-audit-001',
    date: '2024-09-15',
    type: 'register_reconciliation',
    findings: [
      'All entries properly witnessed',
      'Register balances match physical stock',
      'No discrepancies identified'
    ],
    complianceStatus: 'compliant',
    correctiveActions: [],
    pharmacistName: 'Dr. Sarah Johnson',
    pharmacistSignature: 'S.Johnson_20240915',
    nextAuditDate: '2024-09-22'
  },
  {
    id: 's8-audit-002',
    date: '2024-09-10',
    type: 'storage_audit',
    findings: [
      'Drug safe properly secured',
      'Keys stored in secondary safe',
      'Access log maintained',
      'Temperature monitoring active'
    ],
    complianceStatus: 'compliant',
    correctiveActions: [],
    pharmacistName: 'Dr. Sarah Johnson',
    pharmacistSignature: 'S.Johnson_20240910',
    nextAuditDate: '2024-10-10'
  }
];

// Sample Mandatory Notifications
export const mandatoryNotifications: MandatoryNotification[] = [
  {
    id: 'notif-001',
    type: 'significant_departure',
    reporterName: 'Dr. Sarah Johnson',
    reporterRole: 'Pharmacist in Charge',
    practitionerName: 'John Smith',
    practitionerRegistration: 'PHA12345',
    incidentDate: '2024-09-10',
    reportDate: '2024-09-11',
    description: 'Dispensing error resulting in patient receiving incorrect medication strength',
    status: 'submitted',
    urgency: 'within_24_hours',
    evidence: ['Incident report', 'Prescription copy', 'Patient contact record'],
    followUpRequired: true
  }
];

// Sample CPD Records
export const cpdRecords: CPDRecord[] = [
  {
    id: 'cpd-001',
    pharmacistId: 'pharm-001',
    pharmacistName: 'Dr. Sarah Johnson',
    activityType: 'formal_learning',
    activityTitle: 'Advanced Pharmacotherapy in Aged Care',
    provider: 'Pharmaceutical Society of Australia',
    date: '2024-08-15',
    hours: 8,
    learningOutcomes: [
      'Updated knowledge on polypharmacy management',
      'Improved understanding of drug interactions in elderly',
      'Enhanced medication review skills'
    ],
    reflectionNotes: 'This course provided valuable insights into managing complex medication regimens for aged care patients. Will implement new review protocols.',
    evidence: ['Certificate of completion', 'Course materials'],
    cpdYear: '2024'
  },
  {
    id: 'cpd-002',
    pharmacistId: 'pharm-001',
    pharmacistName: 'Dr. Sarah Johnson',
    activityType: 'practice_review',
    activityTitle: 'Medication Safety Audit',
    provider: 'Internal Quality Assurance',
    date: '2024-09-01',
    hours: 4,
    learningOutcomes: [
      'Identified areas for improvement in dispensing workflow',
      'Enhanced error prevention strategies',
      'Improved staff training protocols'
    ],
    reflectionNotes: 'The audit revealed opportunities to strengthen our medication safety processes. Implementing new double-check procedures.',
    evidence: ['Audit report', 'Action plan'],
    cpdYear: '2024'
  }
];

// Compliance Dashboard Metrics
export interface ComplianceDashboardMetrics {
  overallComplianceScore: number;
  totalRequirements: number;
  compliantRequirements: number;
  actionItemsOverdue: number;
  upcomingAudits: number;
  s8ComplianceScore: number;
  cpdCompletionRate: number;
  mandatoryNotificationsSubmitted: number;
}

export const complianceDashboardMetrics: ComplianceDashboardMetrics = {
  overallComplianceScore: 94.5,
  totalRequirements: 6,
  compliantRequirements: 5,
  actionItemsOverdue: 0,
  upcomingAudits: 3,
  s8ComplianceScore: 100,
  cpdCompletionRate: 62.5,
  mandatoryNotificationsSubmitted: 1
};

// Compliance Alerts
export interface ComplianceAlert {
  id: string;
  type: 'audit_due' | 'action_overdue' | 'cpd_deadline' | 's8_discrepancy' | 'notification_required';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  dueDate?: string;
  actionRequired: string;
  regulatoryReference: string;
}

export const complianceAlerts: ComplianceAlert[] = [
  {
    id: 'alert-comp-001',
    type: 'audit_due',
    severity: 'medium',
    title: 'S8 Register Reconciliation Due',
    message: 'Weekly Schedule 8 register reconciliation is due within 2 days',
    dueDate: '2024-09-22',
    actionRequired: 'Complete register reconciliation and document findings',
    regulatoryReference: 'Victorian Pharmacy Authority - Managing Schedule 8 Poisons'
  },
  {
    id: 'alert-comp-002',
    type: 'cpd_deadline',
    severity: 'medium',
    title: 'CPD Hours Remaining',
    message: '15 hours of CPD required to meet annual requirement by December 31',
    dueDate: '2024-12-31',
    actionRequired: 'Plan and complete remaining CPD activities',
    regulatoryReference: 'Pharmacy Board of Australia - CPD Registration Standard'
  },
  {
    id: 'alert-comp-003',
    type: 'action_overdue',
    severity: 'high',
    title: 'Compounding Guidelines Update',
    message: 'Critical action item for updating compounding SOPs is approaching deadline',
    dueDate: '2024-10-31',
    actionRequired: 'Update all compounding procedures to comply with new guidelines',
    regulatoryReference: 'Pharmacy Board of Australia - Guidelines on Compounding of Medicines (1 October 2024)'
  }
];
